import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getSurveys } from '../services/api';
import Card from '../components/Card';
import EffectivenessSlider from '../components/EffectivenessSlider';
import { formatDateTimeEDT } from '../utils/formatters';

// Colors for effectiveness rating
const getEffectivenessColor = (rating) => {
  const numRating = parseInt(rating, 10);
  if (!numRating && numRating !== 0) return 'var(--warning-light)';
  if (numRating >= 8) return 'var(--success-light)';
  if (numRating >= 5) return 'var(--info-light)';
  if (numRating >= 1) return 'var(--warning-light)';
  return 'var(--danger-light)';
};

// Styled components
const SurveysContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SurveysHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const SurveysTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--gray-900);
  margin: 0;
  
  &::after {
    content: '';
    display: block;
    width: 50px;
    height: 4px;
    background-color: var(--primary);
    margin-top: 8px;
    border-radius: 2px;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;
  width: 100%;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.625rem 1rem;
  border: 1px solid var(--border-color, #444);
  border-radius: var(--border-radius);
  font-family: inherit;
  font-size: 0.875rem;
  background-color: var(--bg-primary, #1a1a1a);
  color: var(--text-primary, #f0f0f0);
  
  &::placeholder {
    color: var(--text-secondary, #aaa);
  }
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(26, 54, 93, 0.1);
  }
`;

const SortSelect = styled.select`
  padding: 0.625rem 1rem;
  background-color: var(--bg-primary, #1a1a1a);
  border: 1px solid var(--border-color, #444);
  border-radius: var(--border-radius);
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--text-primary, #f0f0f0);
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(26, 54, 93, 0.1);
  }
  
  option {
    background-color: var(--bg-primary, #1a1a1a);
    color: var(--text-primary, #f0f0f0);
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color, #444);
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary, #aaa);
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PageButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border-color, #444);
  background-color: ${props => props.active ? 'var(--primary)' : 'var(--bg-primary, #1a1a1a)'};
  color: ${props => props.active ? 'white' : 'var(--text-primary, #f0f0f0)'};
  border-radius: var(--border-radius);
  font-size: 0.875rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.active ? 'var(--primary-dark)' : 'var(--bg-hover, #333)'};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(26, 54, 93, 0.1);
  }
`;

const TableHeader = styled.th`
  cursor: ${props => props['data-sortable'] ? 'pointer' : 'default'};
  
  &:hover {
    background-color: ${props => props['data-sortable'] ? 'var(--gray-200)' : 'var(--gray-100)'};
  }
  
  .sort-icon {
    display: inline-block;
    width: 16px;
    height: 16px;
    margin-left: 4px;
    vertical-align: middle;
  }
`;

const SurveysTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th, td {
    padding: 0.75rem 1rem;
    text-align: left;
  }
  
  th {
    background-color: var(--bg-secondary, #2a2a2a);
    font-weight: 600;
    color: var(--text-primary, #f0f0f0);
    font-size: 0.875rem;
  }
  
  tr {
    border-bottom: 1px solid var(--border-color, #444);
  }
  
  tbody tr:hover {
    background-color: var(--card-highlight-bg);
  }
`;

const NoSurveysMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary, #aaa);
  font-size: 1rem;
`;

const ActionButton = styled.button`
  background: var(--primary, #6366f1);
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: background-color 0.2s;
  
  &:hover {
    background: var(--primary-dark, #4f46e5);
  }
`;

const formatEffectiveness = (value) => {
  if (value === null || value === undefined) return null;
  return parseInt(value, 10);
};

function SurveysPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get query parameters with defaults
  const page = parseInt(searchParams.get('page') || '1', 10);
  const search = searchParams.get('search') || '';
  const sortBy = searchParams.get('sort_by') || 'created_at';
  const sortOrder = searchParams.get('sort_order') || 'desc';
  
  const [surveysData, setSurveysData] = useState({
    surveys: [],
    count: 0,
    next: null,
    previous: null,
    currentPage: 1
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Derived state
  const { surveys, count } = surveysData;
  const itemsPerPage = 10; // This should match the backend pagination
  const totalPages = Math.ceil(count / itemsPerPage);
  const hasPreviousPage = page > 1;
  const hasNextPage = page < totalPages;
  
  useEffect(() => {
    fetchSurveysData();
  }, [page, search, sortBy, sortOrder]);
  
  // Function to update URL and refetch data
  const updateQueryParams = (newParams) => {
    const updatedParams = { page, search, sort_by: sortBy, sort_order: sortOrder, ...newParams };
    
    // Remove empty params
    Object.keys(updatedParams).forEach(key => 
      (updatedParams[key] === '' || updatedParams[key] === null) && delete updatedParams[key]
    );
    
    setSearchParams(updatedParams);
  };
  
  // Handle page change
  const handlePageChange = (newPage) => {
    updateQueryParams({ page: newPage });
  };
  
  // Handle search
  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      updateQueryParams({ search: e.target.value, page: 1 });
    }
  };
  
  // Handle sort change
  const handleSortChange = (field) => {
    let newSortOrder = 'asc';
    
    if (field === sortBy) {
      // Toggle sort order if clicking the same field
      newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    }
    
    updateQueryParams({ sort_by: field, sort_order: newSortOrder, page: 1 });
  };
  
  // Get sort indicator for table headers
  const getSortIndicator = (field) => {
    if (field !== sortBy) return null;
    
    return (
      <span className="sort-icon">
        {sortOrder === 'asc' ? '↑' : '↓'}
      </span>
    );
  };
  
  // Generate page list for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    
    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always include first page
      pageNumbers.push(1);
      
      // Complex logic for showing relevant pages
      if (page <= 3) {
        // Near the beginning
        pageNumbers.push(2, 3, 4, '...', totalPages);
      } else if (page >= totalPages - 2) {
        // Near the end
        pageNumbers.push('...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        // Middle - show current page and neighbors
        pageNumbers.push('...', page - 1, page, page + 1, '...', totalPages);
      }
    }
    
    return pageNumbers;
  };
  
  // Fetch surveys data
  const fetchSurveysData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getSurveys({
        page,
        search,
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      setSurveysData(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching surveys:', err);
      setError('Failed to load surveys. Please try again later.');
      setLoading(false);
    }
  };
  
  return (
    <SurveysContainer>
      <Card>
        <SurveysHeader>
          <SurveysTitle>Patient Surveys</SurveysTitle>
        </SurveysHeader>
        
        <SearchContainer>
          <SearchInput
            type="text"
            placeholder="Search surveys by patient name or email..."
            defaultValue={search}
            onKeyDown={handleSearch}
          />
          
          <SortSelect
            value={`${sortBy}:${sortOrder}`}
            onChange={(e) => {
              const [newSortBy, newSortOrder] = e.target.value.split(':');
              updateQueryParams({ sort_by: newSortBy, sort_order: newSortOrder, page: 1 });
            }}
          >
            <option value="created_at:desc">Newest First</option>
            <option value="created_at:asc">Oldest First</option>
            <option value="patient__last_name:asc">Patient Name (A-Z)</option>
            <option value="patient__last_name:desc">Patient Name (Z-A)</option>
            <option value="medicine_effectiveness:desc">Highest Effectiveness</option>
            <option value="medicine_effectiveness:asc">Lowest Effectiveness</option>
          </SortSelect>
        </SearchContainer>
        
        <div>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--primary)' }}>
              <div style={{ display: 'inline-block', animation: 'spin 1s linear infinite', marginBottom: '1rem' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="2" x2="12" y2="6"></line>
                  <line x1="12" y1="18" x2="12" y2="22"></line>
                  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                  <line x1="2" y1="12" x2="6" y2="12"></line>
                  <line x1="18" y1="12" x2="22" y2="12"></line>
                  <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                  <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
                </svg>
              </div>
              <p>Loading surveys...</p>
            </div>
          ) : error ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--danger)' }}>
              <p>{error}</p>
              <button 
                onClick={fetchSurveysData}
                style={{ 
                  marginTop: '1rem',
                  padding: '0.5rem 1rem',
                  background: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Try Again
              </button>
            </div>
          ) : surveys.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              <p>No surveys found. {search && 'Try a different search term.'}</p>
            </div>
          ) : (
            <>
              <SurveysTable>
                <thead>
                  <tr>
                    <TableHeader 
                      data-sortable={true} 
                      onClick={() => handleSortChange('created_at')}
                    >
                      Date {getSortIndicator('created_at')}
                    </TableHeader>
                    <TableHeader 
                      data-sortable={true} 
                      onClick={() => handleSortChange('patient__last_name')}
                    >
                      Patient {getSortIndicator('patient__last_name')}
                    </TableHeader>
                    <TableHeader>Current Medicine</TableHeader>
                    <TableHeader 
                      data-sortable={true} 
                      onClick={() => handleSortChange('medicine_effectiveness')}
                    >
                      Effectiveness {getSortIndicator('medicine_effectiveness')}
                    </TableHeader>
                    <TableHeader>Side Effects</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {surveys.map((survey) => (
                    <tr key={survey.survey_id}>
                      <td>{formatDateTimeEDT(survey.created_at)}</td>
                      <td>
                        {survey.patient ? (
                          <Link 
                            to={`/patients/${survey.patient.patient_id}`}
                            style={{ 
                              color: 'var(--text-primary, #f0f0f0)', 
                              textDecoration: 'none',
                              fontWeight: '500'
                            }}
                            onMouseEnter={(e) => e.target.style.color = 'var(--primary, #6366f1)'}
                            onMouseLeave={(e) => e.target.style.color = 'var(--text-primary, #f0f0f0)'}
                          >
                            {survey.patient.first_name} {survey.patient.last_name}
                          </Link>
                        ) : 'N/A'}
                      </td>
                      <td>{survey.current_medicine || 'N/A'}</td>
                      <td>
                        {survey.medicine_effectiveness !== null && survey.medicine_effectiveness !== undefined ? (
                          <EffectivenessSlider value={formatEffectiveness(survey.medicine_effectiveness)} />
                        ) : (
                          <span style={{ color: 'var(--text-secondary)' }}>N/A</span>
                        )}
                      </td>
                      <td>{survey.experienced_side_effects ? 'Yes' : 'No'}</td>
                      <td>
                        <Link to={`/surveys/${survey.survey_id}`}>
                          <ActionButton>
                            <span>View</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          </ActionButton>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </SurveysTable>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <PaginationContainer>
                  <PaginationInfo>
                    Showing {Math.min((page - 1) * itemsPerPage + 1, surveysData.count)} to {Math.min(page * itemsPerPage, surveysData.count)} of {surveysData.count} surveys
                  </PaginationInfo>
                  
                  <PaginationButtons>
                    <PageButton 
                      onClick={() => handlePageChange(page - 1)}
                      disabled={!hasPreviousPage}
                    >
                      Previous
                    </PageButton>
                    
                    {getPageNumbers().map((pageNum, index) => (
                      typeof pageNum === 'number' ? (
                        <PageButton 
                          key={index}
                          active={pageNum === page}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </PageButton>
                      ) : (
                        <span key={index} style={{ padding: '0.5rem 0.25rem' }}>
                          {pageNum}
                        </span>
                      )
                    ))}
                    
                    <PageButton 
                      onClick={() => handlePageChange(page + 1)}
                      disabled={!hasNextPage}
                    >
                      Next
                    </PageButton>
                  </PaginationButtons>
                </PaginationContainer>
              )}
            </>
          )}
        </div>
      </Card>
    </SurveysContainer>
  );
}

export default SurveysPage;